import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Camera, RotateCcw } from 'lucide-react';
import {
  exitFullscreen,
  expand,
  haptic,
  requestFullscreen,
  setBackButton,
} from '../platform';

function CameraCapture({ onCapture, onClose }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // environment = задняя, user = фронтальная
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(false);

  const requestTelegramFullscreen = () => {
    try {
      expand();
      requestFullscreen();
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    try {
      exitFullscreen();
    } catch (e) {
      /* ignore */
    }

    return () => {
      requestTelegramFullscreen();
      setTimeout(requestTelegramFullscreen, 350);
    };
  }, []);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [facingMode]);

  useEffect(() => {
    setBackButton({ visible: true, onClick: handleClose });

    return () => {
      setBackButton({ visible: false, onClick: handleClose });
    };
  }, [stream]);

  const getVideoConstraints = (withFacingMode = true) => ({
    video: {
      ...(withFacingMode ? { facingMode } : {}),
      width: { ideal: 1920 },
      height: { ideal: 1080 }
    },
    audio: false
  });

  const startCamera = async () => {
    try {
      setError(null);
      setIsReady(false);
      
      // Останавливаем предыдущий поток
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      let mediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia(getVideoConstraints(true));
      } catch (primaryError) {
        console.warn('Camera facing mode failed, retrying generic camera:', primaryError);
        mediaStream = await navigator.mediaDevices.getUserMedia(getVideoConstraints(false));
      }

      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setIsReady(true);
        };
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError(t('camera.accessError', 'Could not access the camera'));
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const switchCamera = () => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  };

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Устанавливаем размер canvas равным видео
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    
    // Если фронтальная камера - отзеркаливаем
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    
    ctx.drawImage(video, 0, 0);
    
    // Конвертируем в blob
    canvas.toBlob((blob) => {
      if (blob) {
        // Создаём File объект
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        requestTelegramFullscreen();
        onCapture(file);
        setTimeout(requestTelegramFullscreen, 350);
      }
    }, 'image/jpeg', 0.9);
    haptic('medium');
  };

  const handleClose = () => {
    stopCamera();
    onClose();
    requestTelegramFullscreen();
    setTimeout(requestTelegramFullscreen, 350);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4">
        <button
          onClick={handleClose}
          className="w-10 h-10 bg-black/50 rounded-full flex items-center justify-center"
        >
          <X className="w-6 h-6 text-white" />
        </button>
        <button
          onClick={switchCamera}
          className="w-10 h-10 bg-black/50 rounded-full flex items-center justify-center"
        >
          <RotateCcw className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Video preview */}
      <div className="flex-1 flex items-center justify-center overflow-hidden">
        {error ? (
          <div className="text-white text-center p-4">
            <p className="mb-4">{error}</p>
            <button
              onClick={startCamera}
              className="bg-[#FFD93D] text-black px-4 py-2 rounded-lg"
            >
              {t('common.retry')}
            </button>
          </div>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{
              transform: facingMode === 'user' ? 'scaleX(-1)' : 'none'
            }}
          />
        )}
      </div>

      {/* Hidden canvas for capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 p-8 flex justify-center">
        <button
          onClick={takePhoto}
          disabled={!isReady}
          className={`w-20 h-20 rounded-full border-4 border-white flex items-center justify-center transition-all ${
            isReady ? 'bg-white/20 active:scale-95' : 'bg-white/10 opacity-50'
          }`}
        >
          <div className="w-16 h-16 bg-white rounded-full" />
        </button>
      </div>
    </div>
  );
}

export default CameraCapture;
